import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import SocialMedia from "elements/social/SocialMedia";
import { FC } from "react";
import { Helmet } from "react-helmet";
import { BiSolidLike } from "react-icons/bi";
import { FaAward, FaHandPointer, FaUsers, FaHeart, FaRocket } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import img1 from "../../images/aboutus1.png";
import img2 from "../../images/aboutus2.png";
import GridSectionHero from "./GridSectionHero";

export interface PageAboutProps {
  className?: string;
}

const PageAbout: FC<PageAboutProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  return (
    <div className={`nc-PageAbout overflow-hidden relative min-h-screen ${className}`} data-nc-id="PageAbout">
      <Helmet>
        <title>
          Espaces événementiels Paris, Île-de-France et partout en France : trouvez le lieu idéal | Event To Bee
        </title>
        <meta
          title="Location salle événement | Event To Bee"
          name="description"
          content="Découvrez la location des espaces événementiels pour votre événement en. Simple et pas cher. Lofts, jardins, salles de réception, studios, etc. Réservez en quelques clics !"
        />
      </Helmet>

      {/* ======== BG GLASS ======== */}
      <BgGlassmorphism />

      {/* Hero Section */}
      <div className="relative pt-16 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900"></div>
        <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-primary-200/30 to-secondary-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-accent-200/20 to-primary-200/20 rounded-full blur-3xl"></div>

        <div className="container relative">
          <div className="text-center max-w-4xl mx-auto animate-fade-in-up">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-full text-sm font-medium text-primary-600 mb-6">
              <span className="w-2 h-2 bg-primary-500 rounded-full mr-2 animate-pulse"></span>À propos d'Event To
              Bee
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent mb-6">
              Votre partenaire événementiel
            </h1>
            <p className="text-xl md:text-2xl text-neutral-600 dark:text-neutral-400 mb-8 leading-relaxed">
              Créez, partagez, célébrez — nous trouvons le lieu parfait pour chaque moment
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
                <FaUsers className="w-5 h-5 text-primary-500 mr-2" />
                <span className="text-neutral-700 dark:text-neutral-300 font-medium">
                  +1000 clients satisfaits
                </span>
              </div>
              <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
                <FaHeart className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-neutral-700 dark:text-neutral-300 font-medium">Événements inoubliables</span>
              </div>
              <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
                <FaRocket className="w-5 h-5 text-accent-500 mr-2" />
                <span className="text-neutral-700 dark:text-neutral-300 font-medium">Innovation constante</span>
              </div>
            </div>
          </div>
          <SocialMedia classOne="mt-12 flex justify-center" classList="social-icon social-default" />
        </div>
      </div>

      {/* Mission Section */}
      <div className="container py-16 space-y-16 lg:py-28 lg:space-y-28">
        <div className="relative animate-fade-in-up animation-delay-200">
          <GridSectionHero
            imgWidth="60%"
            subHeading="Event To Bee est bien plus qu'une simple plateforme de location événementielle ; c'est une passerelle vers des expériences uniques et mémorables. Notre mission est de faciliter la recherche d'espaces atypiques et authentiques pour tous types d'événements, qu'il s'agisse d'un anniversaire, d'un cocktail élégant, d'un shooting photo ou simplement d'une réunion entre amis."
            rightImg={img1}
            btnText=""
            heading="EVENT TO BEE est une jeune plateforme française, qui facilite la recherche et la réservation d'espaces authentiques et atypiques propices à l'organisation d'événements, qu'il s'agisse d'un anniversaire, d'un cocktail élégant, d'un shooting photo ou simplement d'une réunion entre amis..."
          />
        </div>
      </div>

      {/* Notre Histoire Section */}
      <div className="container pt-16 animate-fade-in-up animation-delay-400">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-accent-50 to-primary-50 rounded-full text-sm font-medium text-accent-600 mb-4">
              <span className="w-2 h-2 bg-accent-500 rounded-full mr-2 animate-pulse"></span>
              Notre parcours
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
              L'histoire d'Event To Bee
            </h2>
          </div>

          <div className="space-y-8">
            <div className="relative p-8 bg-white dark:bg-neutral-800 rounded-3xl shadow-xl border border-neutral-200 dark:border-neutral-700 transform hover:scale-[1.02] transition-all duration-300">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                1
              </div>
              <p className="text-lg leading-relaxed text-neutral-700 dark:text-neutral-300">
                Event To Bee est née d'un constat simple : trouver un espace événementiel est souvent un véritable
                parcours du combattant. Entre la recherche d'un lieu adapté, les critères de localisation, de
                capacité, de style, de tarifs et les démarches administratives, tout devient vite complexe.
              </p>
            </div>

            <div className="relative p-8 bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-neutral-800 dark:to-neutral-700 rounded-3xl border border-primary-200 dark:border-neutral-600 transform hover:scale-[1.02] transition-all duration-300">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                2
              </div>
              <p className="text-lg leading-relaxed text-neutral-700 dark:text-neutral-300">
                Nous avons donc créé Event To Bee pour simplifier ce processus. Que vous soyez un professionnel
                cherchant un lieu pour une conférence, un séminaire d'entreprise, un lancement de produit, ou un
                particulier organisant une réception privée ou un mariage, notre plateforme vous offre un accès
                rapide et efficace à une sélection variée d'espaces exceptionnels : lofts, villas, jardins, salles
                de réception, péniches, rooftops, studios photo, etc.
              </p>
            </div>

            <div className="relative p-8 bg-white dark:bg-neutral-800 rounded-3xl shadow-xl border border-neutral-200 dark:border-neutral-700 transform hover:scale-[1.02] transition-all duration-300">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-r from-accent-500 to-primary-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                3
              </div>
              <p className="text-lg leading-relaxed text-neutral-700 dark:text-neutral-300">
                Nous nous engageons à vous offrir une expérience simple, sécurisée et fluide pour trouver le lieu
                idéal correspondant à votre événement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container py-16 mt-16 animate-fade-in-up animation-delay-600">
        <div className="flex flex-col items-center justify-between md:flex-row gap-12">
          <div className="w-full space-y-8 md:w-1/2">
            <div className="text-center md:text-left mb-8">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-secondary-50 to-accent-50 rounded-full text-sm font-medium text-secondary-600 mb-4">
                <span className="w-2 h-2 bg-secondary-500 rounded-full mr-2 animate-pulse"></span>
                Nos avantages
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white">
                La Location de lieux événementiels autrement
              </h2>
            </div>

            <div className="group p-8 bg-white dark:bg-neutral-800 rounded-3xl shadow-xl border border-neutral-200 dark:border-neutral-700 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <FaAward className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">Louez avec confiance</h3>
                  <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    Nous sélectionnons uniquement des lieux vérifiés et sécurisés, avec des hôtes fiables.
                    Participez à un processus simple et rapide, Notre objectif est de vous garantir une expérience
                    de qualité et sans mauvaise surprise. Chaque espace est soigneusement évalué pour répondre aux
                    critères de sécurité, d'accessibilité et de confort.
                  </p>
                </div>
              </div>
            </div>

            <div className="group p-8 bg-gradient-to-br from-secondary-50 to-accent-50 dark:from-neutral-800 dark:to-neutral-700 rounded-3xl border border-secondary-200 dark:border-neutral-600 hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-secondary-500 to-accent-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <FaHandPointer className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
                    Flexibilité et large choix d'espaces
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    Grâce à notre plateforme, vous disposez d'une grande liberté de choix : capacité d'accueil,
                    style de lieu, type d'événement, horaires. Que vous recherchiez un lieu pour un séminaire
                    d'entreprise, un mariage, un shooting photo ou une soirée d'entreprise, vous pouvez comparer
                    les offres, discuter directement avec les hôtes et trouver l'espace qui correspond parfaitement
                    à votre projet.
                  </p>
                </div>
              </div>
            </div>

            <div className="group p-8 bg-white dark:bg-neutral-800 rounded-3xl shadow-xl border border-neutral-200 dark:border-neutral-700 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-primary-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <BiSolidLike className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
                    Trouvez votre lieu idéal facilement
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    La location d'un espace événementiel pour un événement est désormais simplifiée grâce à notre
                    plateforme intuitive. Filtrez par localisation, type de lieu ou capacité d'accueil pour trouver
                    facilement et rapidement l'espace idéal, qu'il s'agisse d'un lieu atypique, d'un espace
                    professionnel ou d'un site prestigieux.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full md:w-1/2 relative">
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src={img2}
                alt="Event Venue"
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="container py-16 animate-fade-in-up animation-delay-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-50 to-accent-50 rounded-full text-sm font-medium text-primary-600 mb-4">
              <span className="w-2 h-2 bg-primary-500 rounded-full mr-2 animate-pulse"></span>
              Confiance et qualité
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
              Pourquoi nous faire confiance pour louer un espace pour votre événement ?
            </h2>
          </div>

          <div className="p-8 bg-gradient-to-br from-white to-neutral-50 dark:from-neutral-800 dark:to-neutral-900 rounded-3xl shadow-xl border border-neutral-200 dark:border-neutral-700">
            <p className="text-lg leading-relaxed text-neutral-700 dark:text-neutral-300">
              Event To Bee, c'est une plateforme reconnue pour la location des espaces événementiels. Nous avons
              aidé des centaines de clients à trouver le lieu parfait pour leurs projets. Nos partenaires sont
              soigneusement sélectionnés pour vous garantir une qualité de service irréprochable, que ce soit pour
              un événement professionnel ou privé. Nous mettons un point d'honneur à vous accompagner tout au long
              du processus de réservation pour que votre expérience soit simple, fluide et sécurisée. En cas de
              besoin, notre service client est là pour vous aider à chaque étape.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container py-16 mt-16 animate-fade-in-up animation-delay-1000">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500 p-12 text-center">
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          <div className="absolute -top-6 -left-6 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>

          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Organisez votre événement avec <br />
              <span className="relative">
                Event To Bee
                <span className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 rounded-full"></span>
              </span>
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
              Ne remettez pas la location de salle pour votre événement à plus tard ! Découvrez dès maintenant
              notre sélection d'espaces uniques et réservez celui qui vous convient en toute simplicité.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <ButtonPrimary
                onClick={() => navigate("/espace-evenementiel")}
                className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-full hover:bg-neutral-100 transform hover:scale-105 transition-all duration-300"
              >
                Explorez les espaces
              </ButtonPrimary>
              <ButtonSecondary
                onClick={() => navigate("/prestations")}
                className="px-8 py-4 bg-white/10 text-white border-white/30 font-semibold rounded-full hover:bg-white/20 transform hover:scale-105 transition-all duration-300"
              >
                Trouvez un prestataire
              </ButtonSecondary>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-primary-200/30 to-secondary-200/30 rounded-full blur-xl"></div>
      <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-accent-200/20 to-primary-200/20 rounded-full blur-xl"></div>
    </div>
  );
};

export default PageAbout;
